import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Send, Sparkles, LayoutGrid, Loader2 } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

const card = "bg-white border border-[var(--line)] rounded-2xl p-5";
const SID = "planner-main";

function Chat() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get(`/ai/planner/history?session_id=${SID}`).then((r) => setMsgs((m) => [...r.data, ...m])).catch(() => {}); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  async function send(q) {
    const message = (q || text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/planner/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: SID, message }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "The planner couldn't reply");
      const rd = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await rd.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const raw = p.replace(/^data: /, "");
          if (raw === "[DONE]") continue;
          try { const j = JSON.parse(raw); if (j.t) setMsgs((m) => (m[m.length - 1]?.role === "assistant" ? [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + j.t }] : [...m, { role: "assistant", text: j.t }])); if (j.error) toast.error(j.error); } catch { /* partial */ }
        }
      }
    } catch (e) { toast.error(e.message); setMsgs((m) => m.slice(0, -1)); } finally { setBusy(false); }
  }
  const ideas = ["Make me a 12-month planning checklist", "Ideas to save money on flowers", "Plan a reception timeline for 6pm to midnight", "How do I seat divorced parents?"];
  return (
    <div className={card} data-testid="ai-planner-chat">
      <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--gold)]" /> Ask your planner</h3>
      <div className="h-[420px] overflow-y-auto space-y-3 mb-3 pr-1">
        {msgs.map((m, i) => <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-[#111] text-white whitespace-pre-wrap" : "bg-[var(--bg-warm)] ai-md"}`} data-testid={`ai-msg-${i}`}>{!m.text ? <Loader2 className="w-4 h-4 animate-spin" /> : m.role === "user" ? m.text : <ReactMarkdown>{m.text}</ReactMarkdown>}</div>)}
        {!msgs.length && <div className="flex flex-wrap gap-2">{ideas.map((q, i) => <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--line)] hover:border-[var(--gold)]" data-testid={`ai-idea-${i}`}>{q}</button>)}</div>}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Ask anything about planning your wedding…" className="input-luxe flex-1" data-testid="ai-chat-input" />
        <button disabled={busy} className="btn-gold text-xs" data-testid="ai-chat-send"><Send className="w-3 h-3" /> Send</button>
      </form>
    </div>
  );
}

function Seating() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  async function gen() {
    setBusy(true);
    try { const { data } = await api.post("/ai/seating/generate"); setD(data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function apply() {
    if (!window.confirm("Seat everyone like this? You can still move people on the Seating Chart page.")) return;
    try { const { data } = await api.post("/ai/seating/apply", { tables: d.tables }); toast.success(`${data.seated} guests seated`); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className={card} data-testid="ai-seating">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-[var(--gold)]" /> AI seating chart</h3>
      <p className="text-xs text-[var(--muted)] mb-3">Groups families and friends together using your guest list.</p>
      <button onClick={gen} disabled={busy} className="btn-outline-gold text-xs" data-testid="ai-seating-generate">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {d ? "Try again" : "Suggest a seating plan"}</button>
      {d && <>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">{d.tables.map((t, i) => <div key={i} className="rounded-xl bg-[var(--bg-warm)] p-3" data-testid={`ai-table-${i}`}><p className="font-medium text-sm mb-1">{t.name}</p><p className="text-xs text-[var(--muted)]">{t.guests.map((g) => g.name).join(", ")}</p></div>)}</div>
        {d.tips.length > 0 && <ul className="text-xs text-[var(--muted)] list-disc pl-5 mt-3">{d.tips.map((x) => <li key={x}>{x}</li>)}</ul>}
        <button onClick={apply} className="btn-gold text-xs mt-4" data-testid="ai-seating-apply">Use this plan</button>
      </>}
    </div>
  );
}

export default function AiPlannerPage() {
  return (
    <div className="space-y-5" data-testid="ai-planner-page">
      <div><p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">ChatGPT</p><h1 className="font-serif font-light text-4xl">AI Planner</h1>
        <p className="text-[var(--muted)] mt-2 text-sm">Ideas, checklists, tips and a seating plan made from your own wedding details.</p></div>
      <div className="grid lg:grid-cols-[1fr_420px] gap-5 items-start"><Chat /><Seating /></div>
    </div>
  );
}
